import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Sparkles, Edit, Plus, Trash2, BookOpen, ClipboardCheck, Presentation, Crown, LayoutGrid, Eye, FolderOpen, RefreshCw, Printer } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from "@/components/ui/context-menu";
import InstructorSkillTreePreview from "./InstructorSkillTreePreview";
import { removeOutlineSectionByChapterId, removeOutlineTopicByNodeId } from "@/lib/outlineMutations";
import { openSkillTreePrintWindow, type SkillTreePrintGroup } from "@/lib/skillTreePrint";

interface SkillNode {
  id: string;
  skill_tree_id: string;
  chapter_id?: string | null;
  title: string;
  description: string | null;
  level_index: number;
  position_index: number;
  is_key_node: boolean;
  content_summary: string | null;
}

interface NodeContent {
  content: string;
  content_type: string;
}

interface Certification {
  name: string;
  issuer: string;
  url?: string;
  domains?: string[];
}

interface SkillTree {
  id: string;
  course_id: string;
  title: string;
  metadata: any;
  target_certifications?: Certification[];
}

interface Prerequisite {
  id: string;
  skill_node_id: string;
  prereq_node_id: string;
}

interface SkillChapter {
  id: string;
  skill_tree_id: string;
  title: string;
  description: string | null;
  order_index: number;
}

interface SkillTreeViewProps {
  courseId: string;
  /** When present, skill tree is built from this outline; "Generate" becomes "Create from outline" */
  addieOutline?: Record<string, unknown> | null;
  onOutlineRefetch?: () => void;
}

const SkillTreeView = ({ courseId, addieOutline, onOutlineRefetch }: SkillTreeViewProps) => {
  const navigate = useNavigate();
  const [tree, setTree] = useState<SkillTree | null>(null);
  const [nodes, setNodes] = useState<SkillNode[]>([]);
  const [chapters, setChapters] = useState<SkillChapter[]>([]);
  const [prerequisites, setPrerequisites] = useState<Prerequisite[]>([]);
  const [loading, setLoading] = useState(true); // Start as true for initial load
  const [refreshing, setRefreshing] = useState(false); // For background refreshes
  const [generating, setGenerating] = useState(false);
  const [selectedNode, setSelectedNode] = useState<SkillNode | null>(null);
  const [editedNode, setEditedNode] = useState<Partial<SkillNode>>({});
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generatingExam, setGeneratingExam] = useState(false);
  const [generatingSlides, setGeneratingSlides] = useState(false);
  const [nodeContent, setNodeContent] = useState<NodeContent | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [nodeSlideDecks, setNodeSlideDecks] = useState<any[]>([]);
  const [showAddNodeDialog, setShowAddNodeDialog] = useState(false);
  const [newNode, setNewNode] = useState({
    title: "",
    description: "",
    level_index: 0,
    position_index: 0,
    is_key_node: false,
  });
  const [creatingNode, setCreatingNode] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "node"; node: SkillNode }
    | { kind: "chapter"; chapter: SkillChapter; nodeIds: string[]; title: string }
    | null
  >(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [viewMode, setViewMode] = useState<"preview" | "list">("preview");
  const [allNodeContent, setAllNodeContent] = useState<Map<string, boolean>>(new Map());
  const [allNodeSlides, setAllNodeSlides] = useState<Map<string, number>>(new Map());
  const [connectingFromHandle, setConnectingFromHandle] = useState<string | null>(null);
  const [contentDepth, setContentDepth] = useState<1 | 2 | 3>(2);
  const [generatingAllLessons, setGeneratingAllLessons] = useState(false);
  const [lessonsProgress, setLessonsProgress] = useState<{ current: number; total: number } | null>(null);
  const [refreshingChapterId, setRefreshingChapterId] = useState<string | null>(null);
  const fetchInProgressRef = useRef(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();

  const fetchSkillTree = async (isRefresh = false) => {
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;

    // Only show blank screen on initial load, not on refreshes
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    // Ensure session is valid
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: "Please sign in again.",
      });
      setLoading(false);
      setRefreshing(false);
      fetchInProgressRef.current = false;
      return;
    }

    const { data, error } = await invokeFunction("get-skill-tree", {
      body: { courseId },
    });

    if (error) {
      console.error("Error fetching skill tree:", error);
      toast({
        variant: "destructive",
        title: "Error loading skill tree",
        description: error.message,
      });
    } else {
      setTree(data.tree);
      setNodes(data.nodes || []);
      setChapters(data.chapters || []);
      setPrerequisites(data.prerequisites || []);
      
      // Fetch content and slides status for all nodes
      if (data.nodes && data.nodes.length > 0) {
        const nodeIds = data.nodes.map((n: SkillNode) => n.id);
        
        // Fetch content status for all nodes
        const { data: contentData } = await supabase
          .from("skill_node_content")
          .select("skill_node_id")
          .in("skill_node_id", nodeIds);
        
        const contentMap = new Map<string, boolean>();
        contentData?.forEach(c => contentMap.set(c.skill_node_id, true));
        setAllNodeContent(contentMap);
        
        // Fetch slide deck counts for all nodes
        const { data: slidesData } = await supabase
          .from("generated_slide_decks")
          .select("skill_node_id")
          .in("skill_node_id", nodeIds);
        
        const slidesMap = new Map<string, number>();
        slidesData?.forEach(s => {
          slidesMap.set(s.skill_node_id, (slidesMap.get(s.skill_node_id) || 0) + 1);
        });
        setAllNodeSlides(slidesMap);
      }
    }
    setLoading(false);
    setRefreshing(false);
    fetchInProgressRef.current = false;
    return data;
  };

  useEffect(() => {
    fetchSkillTree();
  }, [courseId]);

  // Listen for content updates from the Course Assistant chatbot (debounced to avoid duplicate fetches)
  useEffect(() => {
    const handleContentUpdate = () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(() => {
        refreshTimeoutRef.current = null;
        fetchSkillTree(true);
      }, 300);
    };

    window.addEventListener("course-content-updated", handleContentUpdate);
    return () => {
      window.removeEventListener("course-content-updated", handleContentUpdate);
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, [courseId]);

  const handleGenerateSkillTree = async (focusInstructions?: string) => {
    setGenerating(true);
    
    // Ensure session is valid
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: "Please sign in again.",
      });
      setGenerating(false);
      return;
    }

    const { data, error } = await invokeFunction("generate-skill-tree", {
      body: { courseId, ...(focusInstructions && { focusInstructions }) },
    });

    if (error) {
      // Check if this is an upgrade required error
      if (error.upgradeRequired || error.error?.includes("Free tier limit")) {
        setShowUpgradePrompt(true);
        toast({
          variant: "destructive",
          title: "Upgrade Required",
          description: error.message || "You've used your free skill tree. Upgrade to generate more!",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error generating skill tree",
          description: error.message || error.error || "An error occurred",
        });
      }
    } else {
      toast({
        title: "Skill tree generated!",
        description: "Your skill tree has been created from course materials.",
      });
      fetchSkillTree(true); // Refresh without blanking screen
    }
    setGenerating(false);
  };

  const handleCreateNode = async () => {
    if (!tree || !newNode.title.trim()) {
      toast({
        variant: "destructive",
        title: "Title required",
        description: "Please enter a title for the new node.",
      });
      return;
    }

    setCreatingNode(true);
    const { error } = await supabase
      .from("skill_nodes")
      .insert({
        skill_tree_id: tree.id,
        title: newNode.title,
        description: newNode.description || null,
        level_index: newNode.level_index,
        position_index: newNode.position_index,
        is_key_node: newNode.is_key_node,
      });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error creating node",
        description: error.message,
      });
    } else {
      toast({
        title: "Node created",
        description: "New skill node has been added.",
      });
      setShowAddNodeDialog(false);
      setNewNode({
        title: "",
        description: "",
        level_index: 0,
        position_index: 0,
        is_key_node: false,
      });
      fetchSkillTree(true); // Refresh without blanking screen
    }
    setCreatingNode(false);
  };

  const openDeleteNode = (node: SkillNode) => {
    setDeleteTarget({ kind: "node", node });
  };

  const openDeleteChapter = (chapter: SkillChapter, nodesInChapter: SkillNode[]) => {
    setDeleteTarget({
      kind: "chapter",
      chapter,
      nodeIds: nodesInChapter.map((n) => n.id),
      title: chapter.title,
    });
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;

    const sbCh = supabase as { from: (name: string) => ReturnType<typeof supabase.from> };

    setDeleteInProgress(true);
    try {
      if (deleteTarget.kind === "node") {
        const res = await removeOutlineTopicByNodeId(courseId, deleteTarget.node.id);
        if (res.error) throw new Error(res.error);
        if (selectedNode?.id === deleteTarget.node.id) setSelectedNode(null);
        toast({
          title: "Node deleted",
          description: `"${deleteTarget.node.title}" was removed from the skill tree and course outline (when listed).`,
        });
      } else {
        const res = await removeOutlineSectionByChapterId(courseId, deleteTarget.chapter.id);
        if (res.error) {
          const allowLegacy =
            res.error === "No outline found" ||
            /no outline found/i.test(res.error) ||
            res.error === "Chapter not found" ||
            res.error === "Invalid section";
          if (allowLegacy) {
            if (deleteTarget.nodeIds.length > 0) {
              const { error } = await supabase.from("skill_nodes").delete().in("id", deleteTarget.nodeIds);
              if (error) throw new Error(error.message);
            }
            const { error: chErr } = await sbCh.from("skill_tree_chapters").delete().eq("id", deleteTarget.chapter.id);
            if (chErr) throw new Error(chErr.message);
            if (selectedNode && deleteTarget.nodeIds.includes(selectedNode.id)) setSelectedNode(null);
            toast({
              title: "Chapter deleted",
              description:
                deleteTarget.nodeIds.length > 0
                  ? `"${deleteTarget.title}" and ${deleteTarget.nodeIds.length} node(s) were removed. No course outline was updated (none on file).`
                  : `"${deleteTarget.title}" was removed. No course outline was updated (none on file).`,
            });
          } else {
            throw new Error(res.error);
          }
        } else {
          if (selectedNode && deleteTarget.nodeIds.includes(selectedNode.id)) setSelectedNode(null);
          toast({
            title: "Chapter deleted",
            description:
              deleteTarget.nodeIds.length > 0
                ? `"${deleteTarget.title}" was removed from the outline and skill tree (${deleteTarget.nodeIds.length} node(s)).`
                : `"${deleteTarget.title}" was removed from the outline and skill tree.`,
          });
        }
      }
      setDeleteTarget(null);
      window.dispatchEvent(new CustomEvent("course-content-updated"));
      await fetchSkillTree(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Request failed";
      toast({
        variant: "destructive",
        title: deleteTarget.kind === "node" ? "Error deleting node" : "Error deleting chapter",
        description: msg,
      });
    } finally {
      setDeleteInProgress(false);
    }
  };

  const fetchNodeSlides = async (nodeId: string) => {
    const { data, error } = await supabase
      .from('generated_slide_decks')
      .select('*')
      .eq('skill_node_id', nodeId)
      .order('version', { ascending: false });

    if (!error && data) {
      setNodeSlideDecks(data);
    }
  };

  const handleEditNode = async (node: SkillNode) => {
    setSelectedNode(node);
    setEditedNode(node);
    setNodeContent(null);
    setNodeSlideDecks([]);
    
    // Fetch node content and slides
    setLoadingContent(true);
    const { data, error } = await supabase.functions.invoke("get-skill-node-detail", {
      body: { skillNodeId: node.id },
    });
    
    if (!error && data?.content) {
      setNodeContent(data.content);
    }
    setLoadingContent(false);
    
    // Fetch slides for this node
    fetchNodeSlides(node.id);
  };

  const handleSaveNode = async () => {
    if (!selectedNode) return;

    const { error } = await supabase.functions.invoke("update-skill-node", {
      body: {
        nodeId: selectedNode.id,
        ...editedNode,
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error updating node",
        description: error.message,
      });
    } else {
      toast({
        title: "Node updated",
        description: "Your changes have been saved.",
      });
      setSelectedNode(null);
      fetchSkillTree(true); // Refresh without blanking screen
    }
  };

  const handleTogglePrerequisite = async (prereqNodeId: string) => {
    if (!selectedNode) return;

    const existingPrereq = prerequisites.find(
      (p) => p.skill_node_id === selectedNode.id && p.prereq_node_id === prereqNodeId
    );

    const action = existingPrereq ? "delete" : "add";

    const { error } = await supabase.functions.invoke("manage-prerequisites", {
      body: {
        action,
        nodeId: selectedNode.id,
        prereqNodeId,
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error updating prerequisites",
        description: error.message,
      });
    } else {
      fetchSkillTree(true); // Refresh without blanking screen
    }
  };

  const handleConnectionHandleDragStart = (e: React.DragEvent, nodeId: string) => {
    setConnectingFromHandle(nodeId);
    e.dataTransfer.effectAllowed = "link";
    e.dataTransfer.setData("text/plain", nodeId);
    e.stopPropagation(); // Prevent triggering node click
  };

  const handleConnectionHandleDrop = async (e: React.DragEvent, targetNodeId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!connectingFromHandle || connectingFromHandle === targetNodeId) {
      setConnectingFromHandle(null);
      return;
    }

    // Check if link already exists
    const existingPrereq = prerequisites.find(
      (p) => p.skill_node_id === targetNodeId && p.prereq_node_id === connectingFromHandle
    );
    
    if (existingPrereq) {
      toast({
        variant: "destructive",
        title: "Link already exists",
        description: "These nodes are already linked.",
      });
      setConnectingFromHandle(null);
      return;
    }

    // Create the prerequisite link
    const { error } = await supabase.functions.invoke("manage-prerequisites", {
      body: {
        action: "add",
        nodeId: targetNodeId,
        prereqNodeId: connectingFromHandle,
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error creating link",
        description: error.message,
      });
    } else {
      toast({
        title: "Link created",
        description: "Prerequisite link has been added.",
      });
      fetchSkillTree(true); // Refresh without blanking screen
    }
    
    setConnectingFromHandle(null);
  };

  const handleConnectionHandleDragEnd = () => {
    setConnectingFromHandle(null);
    // Small delay to allow drag leave events to fire
    setTimeout(() => {
      // This will be cleared by the component's drag leave handler
    }, 0);
  };

  const handleRemoveLink = async (fromNodeId: string, toNodeId: string) => {
    const { error } = await supabase.functions.invoke("manage-prerequisites", {
      body: {
        action: "delete",
        nodeId: toNodeId,
        prereqNodeId: fromNodeId,
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error removing link",
        description: error.message,
      });
    } else {
      toast({
        title: "Link removed",
        description: "Prerequisite link has been removed.",
      });
      fetchSkillTree(true); // Refresh without blanking screen
    }
  };

  const handleCreateNodeAtPosition = () => {
    setShowAddNodeDialog(true);
  };

  const handleGenerateContent = async () => {
    if (!selectedNode) return;

    setGeneratingContent(true);
    const { error } = await supabase.functions.invoke("generate-node-content", {
      body: { skillNodeId: selectedNode.id, contentDepth },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error generating content",
        description: error.message,
      });
    } else {
      toast({
        title: "Content generated!",
        description: "Lesson content has been created for this node.",
      });
      // Refresh content and skill tree in background
      if (selectedNode) {
        const { data } = await supabase.functions.invoke("get-skill-node-detail", {
          body: { skillNodeId: selectedNode.id },
        });
        if (data?.content) {
          setNodeContent(data.content);
        }
      }
      fetchSkillTree(true); // Refresh tree status without blanking screen
    }
    setGeneratingContent(false);
  };

  const handleGenerateExam = async () => {
    if (!selectedNode) return;

    if (!selectedNode.is_key_node) {
      toast({
        variant: "destructive",
        title: "Not a key node",
        description: "Only key nodes can have exams.",
      });
      return;
    }

    setGeneratingExam(true);
    const { error } = await supabase.functions.invoke("generate-node-exam", {
      body: { skillNodeId: selectedNode.id },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error generating exam",
        description: error.message,
      });
    } else {
      toast({
        title: "Exam generated!",
        description: "Exam questions have been created for this key node.",
      });
      fetchSkillTree(true); // Refresh tree status without blanking screen
    }
    setGeneratingExam(false);
  };

  const handleGenerateSlides = async () => {
    if (!selectedNode) return;
    
    setGeneratingSlides(true);
    const { data, error } = await supabase.functions.invoke("generate-node-slides", {
      body: { 
        skillNodeId: selectedNode.id,
        minSlides: 3,
        maxSlides: 8
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error generating slides",
        description: error.message,
      });
    } else {
      toast({
        title: "Slides generated!",
        description: "Presentation slides have been created for this node.",
      });
      
      // Refresh the slides list and skill tree in background
      if (selectedNode) {
        fetchNodeSlides(selectedNode.id);
      }
      fetchSkillTree(true); // Refresh tree status without blanking screen
    }
    setGeneratingSlides(false);
  };

  // When we have chapters and nodes with chapter_id, group by chapter; otherwise by level
  const sortedChapters = [...chapters].sort((a, b) => a.order_index - b.order_index);
  const useChapterGrouping = sortedChapters.length > 0 && nodes.some((n) => n.chapter_id);

  const nodesByLevel = nodes.reduce((acc, node) => {
    if (!acc[node.level_index]) {
      acc[node.level_index] = [];
    }
    acc[node.level_index].push(node);
    return acc;
  }, {} as Record<number, SkillNode[]>);

  const levels = Object.keys(nodesByLevel)
    .map(Number)
    .sort((a, b) => a - b);

  // Chapter-based grouping for display (chapter order -> nodes)
  const chaptersWithNodes = useChapterGrouping
    ? sortedChapters.map((ch) => ({
        chapter: ch,
        nodes: nodes
          .filter((n) => n.chapter_id === ch.id)
          .sort((a, b) => a.position_index - b.position_index),
      }))
    : [];

  const handleOpenPrintView = () => {
    if (!tree) return;

    const nodeIdToTitle = new Map(nodes.map((n) => [n.id, n.title]));
    const prereqTitlesFor = (nodeId: string) =>
      prerequisites
        .filter((p) => p.skill_node_id === nodeId)
        .map((p) => nodeIdToTitle.get(p.prereq_node_id))
        .filter((t): t is string => Boolean(t));

    const toPrintNode = (n: SkillNode) => ({
      title: n.title,
      description: n.description,
      isKeyNode: n.is_key_node,
      contentSummary: n.content_summary,
      prereqTitles: prereqTitlesFor(n.id),
    });

    let groups: SkillTreePrintGroup[];

    if (nodes.length === 0 && sortedChapters.length > 0) {
      groups = sortedChapters.map((ch, i) => ({
        kind: "chapter",
        badge: `Ch. ${i + 1}`,
        title: ch.title,
        description: ch.description,
        nodes: [],
      }));
    } else if (useChapterGrouping) {
      groups = sortedChapters.map((ch, i) => ({
        kind: "chapter",
        badge: `Ch. ${i + 1}`,
        title: ch.title,
        description: ch.description,
        nodes: nodes
          .filter((n) => n.chapter_id === ch.id)
          .sort((a, b) => a.position_index - b.position_index)
          .map(toPrintNode),
      }));
      const assigned = new Set<string>();
      sortedChapters.forEach((ch) => {
        nodes.filter((n) => n.chapter_id === ch.id).forEach((n) => assigned.add(n.id));
      });
      const unassigned = nodes
        .filter((n) => !assigned.has(n.id))
        .sort((a, b) => a.level_index - b.level_index || a.position_index - b.position_index);
      if (unassigned.length > 0) {
        groups.push({
          kind: "level",
          badge: "Other",
          title: "Nodes not assigned to a chapter",
          description: null,
          nodes: unassigned.map(toPrintNode),
        });
      }
    } else {
      groups = levels.map((levelIndex) => ({
        kind: "level",
        badge: `Level ${levelIndex + 1}`,
        title: `Level ${levelIndex + 1}`,
        description: undefined,
        nodes: (nodesByLevel[levelIndex] || [])
          .slice()
          .sort((a, b) => a.position_index - b.position_index)
          .map(toPrintNode),
      }));
    }

    if (groups.length === 0 && nodes.length === 0) {
      groups = [
        {
          kind: "level",
          badge: "—",
          title: "Skill tree",
          description: "No chapters or nodes yet. Generate the skill tree to populate this document.",
          nodes: [],
        },
      ];
    }

    const opened = openSkillTreePrintWindow({
      treeTitle: tree.title,
      groups,
      nodeCount: nodes.length,
      prereqLinkCount: prerequisites.length,
    });
    if (!opened) {
      toast({
        variant: "destructive",
        title: "Pop-up blocked",
        description: "Allow pop-ups for this site to open the printable skill tree.",
      });
    }
  };

  const deleteConfirmDialog = (
    <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && !deleteInProgress && setDeleteTarget(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {deleteTarget?.kind === "chapter" ? "Delete chapter?" : "Delete skill node?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {deleteTarget?.kind === "chapter" ? (
              deleteTarget.nodeIds.length > 0 ? (
                <>
                  This removes the chapter from the <strong>course outline</strong> and the skill tree, including all{" "}
                  {deleteTarget.nodeIds.length} node(s), lessons, slides, and exams. Prerequisite links involving those
                  nodes will be cleared. This cannot be undone.
                </>
              ) : (
                <>
                  This removes the chapter from the <strong>course outline</strong> and the skill tree. This cannot be
                  undone.
                </>
              )
            ) : deleteTarget?.kind === "node" ? (
              <>
                Removes this node from the skill tree and the matching topic from the <strong>course outline</strong> (if
                present). Lesson content, slides, and exams for this node will be deleted. This cannot be undone.
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteInProgress}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={deleteInProgress}
            onClick={(e) => {
              e.preventDefault();
              void executeDelete();
            }}
          >
            {deleteInProgress ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Loading skill tree...</p>
      </div>
    );
  }

  if (!tree) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Skill Tree</CardTitle>
          <CardDescription>
            {addieOutline
              ? "Create a skill tree from your course outline. The structure will follow your ADDIE design."
              : "Add a course outline first (Course Outline in the sidebar), then create a skill tree from it."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => handleGenerateSkillTree()} disabled={generating || !addieOutline}>
            <Sparkles className="mr-2 h-4 w-4" />
            {generating ? "Creating..." : addieOutline ? "Create from course outline" : "Need course outline first"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Scaffold: tree exists with chapters from outline, but no nodes yet — generate nodes under each chapter
  if (chapters.length > 0 && nodes.length === 0) {
    return (
      <>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">{tree.title}</h2>
            <p className="text-muted-foreground text-sm">
              Chapters from your course outline. Generate nodes to create lessons under each chapter.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button type="button" variant="outline" onClick={handleOpenPrintView} className="gap-2">
              <Printer className="h-4 w-4" />
              Print outline
            </Button>
            <Button onClick={() => handleGenerateSkillTree()} disabled={generating} className="gap-2">
              <Sparkles className="h-4 w-4" />
              {generating ? "Generating nodes..." : "Generate nodes"}
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Chapters ({chapters.length})
          </h3>
          <div className="grid gap-3 md:grid-cols-2">
            {chapters.map((ch, i) => (
              <Card key={ch.id} className="bg-muted/30">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-muted-foreground shrink-0">Ch. {i + 1}</span>
                      <CardTitle className="text-base">{ch.title}</CardTitle>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => openDeleteChapter(ch, [])}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                  {ch.description && (
                    <CardDescription className="line-clamp-2">{ch.description}</CardDescription>
                  )}
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </div>
      {deleteConfirmDialog}
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold">{tree.title}</h2>
            {refreshing && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            )}
          </div>
          <p className="text-muted-foreground text-sm">
            {nodes.length} skill nodes across {useChapterGrouping ? chaptersWithNodes.length : levels.length} {useChapterGrouping ? "chapters" : "levels"}
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showAddNodeDialog} onOpenChange={setShowAddNodeDialog}>
            <Button onClick={() => setShowAddNodeDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Node
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Skill Node</DialogTitle>
                <DialogDescription>
                  Create a new skill node for this course's learning path.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="new-title">Title</Label>
                  <Input
                    id="new-title"
                    placeholder="e.g., Introduction to Variables"
                    value={newNode.title}
                    onChange={(e) => setNewNode({ ...newNode, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-description">Description</Label>
                  <Textarea
                    id="new-description"
                    placeholder="Describe what learners will learn in this node..."
                    value={newNode.description}
                    onChange={(e) => setNewNode({ ...newNode, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-level">Level</Label>
                    <Select
                      value={String(newNode.level_index)}
                      onValueChange={(value) => setNewNode({ ...newNode, level_index: Number(value) })}
                    >
                      <SelectTrigger id="new-level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...levels, levels.length > 0 ? Math.max(...levels) + 1 : 0].map((level) => (
                          <SelectItem key={level} value={String(level)}>
                            Level {level + 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-position">Position</Label>
                    <Input
                      id="new-position"
                      type="number"
                      min={0}
                      value={newNode.position_index}
                      onChange={(e) => setNewNode({ ...newNode, position_index: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="new-is-key-node">Key Exam Node</Label>
                  <Switch
                    id="new-is-key-node"
                    checked={newNode.is_key_node}
                    onCheckedChange={(checked) => setNewNode({ ...newNode, is_key_node: checked })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddNodeDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateNode} disabled={creatingNode}>
                  {creatingNode ? "Creating..." : "Create Node"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            onClick={() => handleGenerateSkillTree("Incorporate recently uploaded materials into the skill tree. Update node content to reflect new sources.")}
            variant="outline"
            disabled={generating}
            title="Regenerate skill tree using latest course materials"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Refreshing..." : "Refresh with materials"}
          </Button>
          <Button onClick={() => handleGenerateSkillTree()} variant="outline" disabled={generating}>
            <Sparkles className="mr-2 h-4 w-4" />
            {generating ? "Regenerating..." : "Regenerate"}
          </Button>
          <Button type="button" variant="outline" onClick={handleOpenPrintView} className="gap-2">
            <Printer className="h-4 w-4" />
            Print tree
          </Button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <Tabs value={viewMode} onValueChange={(v) => {
        setViewMode(v as "preview" | "list");
      }} className="w-full">
        <TabsList className="grid w-full max-w-[300px] grid-cols-2">
          <TabsTrigger value="preview" className="gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="list" className="gap-2">
            <LayoutGrid className="h-4 w-4" />
            List View
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Preview View - Visual Tree */}
      {viewMode === "preview" && (
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div>
              <InstructorSkillTreePreview
                nodes={nodes}
                chapters={useChapterGrouping ? sortedChapters : undefined}
                prerequisites={prerequisites}
                onNodeClick={handleEditNode}
                treeTitle={tree.title}
                nodeContent={allNodeContent}
                nodeSlides={allNodeSlides}
                onConnectionHandleDragStart={handleConnectionHandleDragStart}
                onConnectionHandleDrop={handleConnectionHandleDrop}
                onConnectionHandleDragEnd={handleConnectionHandleDragEnd}
                connectingFromHandleId={connectingFromHandle}
                onRemoveLink={handleRemoveLink}
                onDeleteNode={openDeleteNode}
                onDeleteChapter={useChapterGrouping ? openDeleteChapter : undefined}
              />
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem onSelect={handleCreateNodeAtPosition}>
              <Plus className="mr-2 h-4 w-4" />
              Add New Node
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      )}

      {/* List View - Card Grid */}
      {viewMode === "list" && (
        <div className="space-y-8">
          {useChapterGrouping
            ? chaptersWithNodes.map(({ chapter, nodes: chapterNodes }, idx) => (
                <div key={chapter.id}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <span className="text-muted-foreground font-normal">Ch. {idx + 1}</span>
                      {chapter.title}
                    </h3>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={generating || refreshingChapterId !== null}
                        onClick={() => {
                          setRefreshingChapterId(chapter.id);
                          handleGenerateSkillTree(
                            `Prioritize refreshing chapter "${chapter.title}" with recently uploaded materials. Update this chapter's nodes to incorporate new sources.`
                          ).finally(() => setRefreshingChapterId(null));
                        }}
                        title="Refresh this chapter with recently uploaded materials"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshingChapterId === chapter.id ? "animate-spin" : ""}`} />
                        Refresh with materials
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => openDeleteChapter(chapter, chapterNodes)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Delete chapter
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {chapterNodes.map((node) => {
                      const nodePrereqs = prerequisites.filter((p) => p.skill_node_id === node.id);
                      const hasContent = allNodeContent.get(node.id);
                      const slideCount = allNodeSlides.get(node.id) || 0;
                      return (
                        <Card
                          key={node.id}
                          className="cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => handleEditNode(node)}
                          data-tutorial="skill-node"
                        >
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <CardTitle className="text-base flex-1">{node.title}</CardTitle>
                              <div className="flex gap-1">
                                {node.is_key_node && (
                                  <Badge variant="default">Key Node</Badge>
                                )}
                                {hasContent && slideCount > 0 ? (
                                  <Badge variant="secondary" className="bg-green-500/20 text-green-700">Ready</Badge>
                                ) : (hasContent || slideCount > 0) ? (
                                  <Badge variant="secondary" className="bg-orange-500/20 text-orange-700">Partial</Badge>
                                ) : null}
                              </div>
                            </div>
                            <CardDescription className="line-clamp-2">
                              {node.description || "No description"}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {nodePrereqs.length > 0 ? (
                                <span>{nodePrereqs.length} prerequisite(s)</span>
                              ) : (
                                <span>No prerequisites</span>
                              )}
                            </div>
                            <div
                              className="flex justify-end mt-3 pt-3 border-t border-border/60"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => openDeleteNode(node)}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete node
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))
            : levels.map((levelIndex) => {
            const levelNodes = nodesByLevel[levelIndex].sort((a, b) => a.position_index - b.position_index);
            return (
              <div key={levelIndex}>
                <h3 className="text-lg font-semibold mb-4">Level {levelIndex + 1}</h3>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {levelNodes.map((node) => {
                      const nodePrereqs = prerequisites.filter((p) => p.skill_node_id === node.id);
                      const hasContent = allNodeContent.get(node.id);
                      const slideCount = allNodeSlides.get(node.id) || 0;
                      
                      return (
                        <Card
                          key={node.id}
                          className="cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => handleEditNode(node)}
                          data-tutorial="skill-node"
                        >
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-base flex-1">{node.title}</CardTitle>
                          <div className="flex gap-1">
                            {node.is_key_node && (
                              <Badge variant="default">Key Node</Badge>
                            )}
                            {hasContent && slideCount > 0 ? (
                              <Badge variant="secondary" className="bg-green-500/20 text-green-700">Ready</Badge>
                            ) : (hasContent || slideCount > 0) ? (
                              <Badge variant="secondary" className="bg-orange-500/20 text-orange-700">Partial</Badge>
                            ) : null}
                          </div>
                        </div>
                        <CardDescription className="line-clamp-2">
                          {node.description || "No description"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Edit className="h-3 w-3" />
                          <span>Click to edit</span>
                        </div>
                        <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                          {hasContent && <span className="text-green-600">✓ Lesson</span>}
                          {slideCount > 0 && <span className="text-green-600">✓ {slideCount} Slides</span>}
                          {nodePrereqs.length > 0 && <span>{nodePrereqs.length} prereq(s)</span>}
                        </div>
                        <div
                          className="flex justify-end mt-3 pt-3 border-t border-border/60"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => openDeleteNode(node)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete node
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Sheet open={!!selectedNode} onOpenChange={() => setSelectedNode(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Skill Node</SheetTitle>
            <SheetDescription>
              Update node details and manage prerequisites
            </SheetDescription>
          </SheetHeader>

          {selectedNode && (
            <div className="space-y-6 mt-6">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={editedNode.title || ""}
                  onChange={(e) => setEditedNode({ ...editedNode, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editedNode.description || ""}
                  onChange={(e) => setEditedNode({ ...editedNode, description: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content_summary">Content Summary</Label>
                <Textarea
                  id="content_summary"
                  value={editedNode.content_summary || ""}
                  onChange={(e) => setEditedNode({ ...editedNode, content_summary: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_key_node">Key Exam Node</Label>
                <Switch
                  id="is_key_node"
                  checked={editedNode.is_key_node || false}
                  onCheckedChange={(checked) => setEditedNode({ ...editedNode, is_key_node: checked })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="level_index">Level</Label>
                  <Select
                    value={String(editedNode.level_index ?? 0)}
                    onValueChange={(value) => setEditedNode({ ...editedNode, level_index: Number(value) })}
                  >
                    <SelectTrigger id="level_index">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((level) => (
                        <SelectItem key={level} value={String(level)}>
                          Level {level + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position_index">Position</Label>
                  <Input
                    id="position_index"
                    type="number"
                    value={editedNode.position_index ?? 0}
                    onChange={(e) => setEditedNode({ ...editedNode, position_index: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Prerequisites</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Select which nodes must be completed before this one
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {nodes
                    .filter((n) => n.id !== selectedNode.id)
                    .map((node) => {
                      const isPrereq = prerequisites.some(
                        (p) => p.skill_node_id === selectedNode.id && p.prereq_node_id === node.id
                      );
                      return (
                        <div key={node.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`prereq-${node.id}`}
                            checked={isPrereq}
                            onChange={() => handleTogglePrerequisite(node.id)}
                            className="rounded"
                          />
                          <label
                            htmlFor={`prereq-${node.id}`}
                            className="text-sm cursor-pointer"
                          >
                            {node.title} (Level {node.level_index + 1})
                          </label>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Content Management Buttons */}
              <div className="space-y-3">
                <Label>Content Management</Label>
                <p className="text-sm text-muted-foreground">
                  View, edit, and generate content for this node
                </p>
                <div className="grid gap-2">
                  <Button
                    onClick={() => navigate(`/project/${courseId}/node/${selectedNode?.id}/lesson`)}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <BookOpen className="mr-2 h-4 w-4 text-blue-500" />
                    Manage Lesson
                    {nodeContent && (
                      <Badge variant="secondary" className="ml-auto">Has Content</Badge>
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => navigate(`/project/${courseId}/node/${selectedNode?.id}/exam`)}
                    variant="outline"
                    className="w-full justify-start"
                    disabled={!selectedNode?.is_key_node}
                  >
                    <ClipboardCheck className="mr-2 h-4 w-4 text-green-500" />
                    Manage Exam
                    {!selectedNode?.is_key_node && (
                      <span className="ml-auto text-xs text-muted-foreground">Key nodes only</span>
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => navigate(`/project/${courseId}/node/${selectedNode?.id}/slides`)}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Presentation className="mr-2 h-4 w-4 text-purple-500" />
                    Manage Slides
                    {nodeSlideDecks.length > 0 && (
                      <Badge variant="secondary" className="ml-auto">{nodeSlideDecks.length} deck(s)</Badge>
                    )}
                  </Button>
                </div>
              </div>

              {/* Quick AI Generation */}
              <div className="space-y-2 border-t pt-4" data-tutorial="generate-content">
                <Label>Quick AI Generation</Label>
                <p className="text-xs text-muted-foreground">
                  Generate content without leaving this page
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={String(contentDepth)}
                    onValueChange={(v) => setContentDepth(Number(v) as 1 | 2 | 3)}
                    disabled={generatingContent}
                  >
                    <SelectTrigger className="w-[130px] h-8">
                      <SelectValue placeholder="Depth" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Level 1: Basic</SelectItem>
                      <SelectItem value="2">Level 2: Standard</SelectItem>
                      <SelectItem value="3">Level 3: In-depth</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleGenerateContent}
                    disabled={generatingContent}
                    variant="secondary"
                    size="sm"
                    title={nodeContent ? "Regenerate lesson using latest materials" : "Generate lesson from course materials"}
                  >
                    <Sparkles className="mr-2 h-3 w-3" />
                    {generatingContent ? "Generating..." : nodeContent ? "Refresh with materials" : "Lesson"}
                  </Button>
                  {selectedNode?.is_key_node && (
                    <Button
                      onClick={handleGenerateExam}
                      disabled={generatingExam}
                      variant="secondary"
                      size="sm"
                    >
                      <Sparkles className="mr-2 h-3 w-3" />
                      {generatingExam ? "Generating..." : "Exam"}
                    </Button>
                  )}
                  <Button
                    onClick={handleGenerateSlides}
                    disabled={generatingSlides}
                    variant="secondary"
                    size="sm"
                  >
                    <Sparkles className="mr-2 h-3 w-3" />
                    {generatingSlides ? "Generating..." : "Slides"}
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveNode} className="flex-1">
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={() => selectedNode && openDeleteNode(selectedNode)}
                  title="Delete this node"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {deleteConfirmDialog}

      {/* Upgrade Prompt Dialog */}
      <Dialog open={showUpgradePrompt} onOpenChange={setShowUpgradePrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Upgrade to Pro
            </DialogTitle>
            <DialogDescription>
              You've used your free skill tree generation. Upgrade to a paid plan to unlock unlimited skill trees and access all premium features.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 p-4">
              <h4 className="font-semibold mb-2">Pro Plan Benefits:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>✓ Unlimited skill tree generations</li>
                <li>✓ AI-powered lesson content</li>
                <li>✓ Automated exam creation</li>
                <li>✓ Slide deck generation</li>
                <li>✓ Priority support</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowUpgradePrompt(false)}>
              Maybe Later
            </Button>
            <Button onClick={() => navigate("/request-demo")} className="gap-2">
              <Crown className="h-4 w-4" />
              Request demo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SkillTreeView;
